import { createContext, useContext, useState, useEffect } from 'react';
import CartService from '../services/cart.service';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState({ items: [], total_price: 0 });
    const { user } = useAuth();

    useEffect(() => {
        if (user) {
            fetchCart();
        } else {
            setCart({ items: [], total_price: 0 });
        }
    }, [user]);

    const fetchCart = async () => {
        try {
            const response = await CartService.getCart();
            // Default to empty structure if response is null/undefined or missing items
            const cartData = response.data || { active_items: [], total: 0 };
            setCart({
                items: cartData.active_items || [],
                total_price: cartData.total || 0,
                ...cartData
            });
        } catch (error) {
            console.error('Failed to fetch cart', error);
            // Fallback
            setCart({ items: [], total_price: 0 });
        }
    };

    const addToCart = async (variantId, quantity = 1) => {
        if (!user) {
            toast.error('Please login to add items to cart');
            return;
        }
        try {
            await CartService.addToCart({ variant_id: variantId, quantity });
            await fetchCart();
            toast.success('Added to cart');
        } catch (error) {
            console.error(error);
            toast.error('Failed to add item');
        }
    };

    const removeFromCart = async (itemId) => {
        try {
            await CartService.removeItem(itemId);
            await fetchCart();
            toast.success('Removed from cart');
        } catch (error) {
            console.error(error);
            toast.error('Failed to remove item');
        }
    };

    const updateQuantity = async (itemId, quantity) => {
        try {
            await CartService.updateItem(itemId, quantity);
            await fetchCart();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, fetchCart }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
